import { createSlice } from "@reduxjs/toolkit";
import {
  getCategoriesBuilder,
  getFeaturedCategoriesBuilder,
  createCategoryBuilder,
  deleteCategoryBuilder,
  toggleCategoryStatusBuilder,
  editCategoryBuilder,
  getProductsBuilder,
  getFeaturedProductsBuilder,
  getProductBuilder,
  createProductBuilder,
  editProductBuilder,
  deleteProductBuilder,
  getLowStockProductsBuilder,
  getSimilarProductsBuilder,
  getProductDimensionsBuilder,
  saveProductDimensionsBuilder,
  deleteProductDimensionsBuilder,
} from "./builder";

const initialState = {
  // Categories state
  categories: {
    data: [],
    loading: false,
    createLoading: false,
    deleteLoading: false,
    toggleLoading: false,
    editLoading: false,
    error: null,
  },

  // Featured categories state
  featuredCategories: {
    data: [],
    loading: false,
    error: null,
  },

  // Main products state
  products: {
    data: [],
    total: 0,
    page: 1,
    perPage: 50,
    maxReached: false,
    loading: false,
    createLoading: false,
    editLoading: false,
    deleteLoading: false,
    error: null,
  },

  // Selected product state
  selectedProduct: {
    data: null,
    loading: false,
    error: null,
  },

  // Featured products state
  featuredProducts: {
    data: [],
    loading: false,
    error: null,
  },

  // Low stock products state
  lowStockProducts: {
    data: [],
    loading: false,
    error: null,
  },

  // Similar products state
  similarProducts: {
    data: [],
    loading: false,
    error: null,
  },

  // Product Dimensions state
  productDimensions: {
    data: null,
    loading: false,
    saveLoading: false,
    deleteLoading: false,
    error: null,
  },
};

export const productsSlice = createSlice({
  name: "productsInfo",
  initialState,
  reducers: {
    // Product actions
    clearSelectedProduct: (state) => {
      state.selectedProduct.data = null;
      state.selectedProduct.error = null;
    },

    // Error clearing actions
    clearProductsError: (state) => {
      state.products.error = null;
    },
    clearCategoriesError: (state) => {
      state.categories.error = null;
    },
    clearSelectedProductError: (state) => {
      state.selectedProduct.error = null;
    },
    clearFeaturedProductsError: (state) => {
      state.featuredProducts.error = null;
    },
    clearFeaturedCategoriesError: (state) => {
      state.featuredCategories.error = null;
    },
    clearProductDimensionsError: (state) => {
      state.productDimensions.error = null;
    },

    // Pagination reset actions
    resetProductsPagination: (state) => {
      state.products.page = 1;
      state.products.maxReached = false;
    },
  },

  extraReducers: (builder) => {
    // Categories builders
    getCategoriesBuilder(builder);
    getFeaturedCategoriesBuilder(builder);
    createCategoryBuilder(builder);
    deleteCategoryBuilder(builder);
    toggleCategoryStatusBuilder(builder);
    editCategoryBuilder(builder);

    // Products builders
    getProductsBuilder(builder);
    getFeaturedProductsBuilder(builder);
    getProductBuilder(builder);
    createProductBuilder(builder);
    editProductBuilder(builder);
    deleteProductBuilder(builder);
    getLowStockProductsBuilder(builder);
    getSimilarProductsBuilder(builder);

    // Product Dimensions builders
    getProductDimensionsBuilder(builder);
    saveProductDimensionsBuilder(builder);
    deleteProductDimensionsBuilder(builder);
  },
});

export const {
  // Product actions
  clearSelectedProduct,

  // Error clearing actions
  clearProductsError,
  clearCategoriesError,
  clearSelectedProductError,
  clearFeaturedProductsError,
  clearFeaturedCategoriesError,
  clearProductDimensionsError,

  // Pagination actions
  resetProductsPagination,
} = productsSlice.actions;

// Selectors
export const selectCategories = (state) => state.productsInfo.categories;
export const selectFeaturedCategories = (state) =>
  state.productsInfo.featuredCategories;
export const selectProducts = (state) => state.productsInfo.products;
export const selectFeaturedProducts = (state) =>
  state.productsInfo.featuredProducts;
export const selectSelectedProduct = (state) =>
  state.productsInfo.selectedProduct;
export const selectLowStockProducts = (state) =>
  state.productsInfo.lowStockProducts;
export const selectSimilarProducts = (state) =>
  state.productsInfo.similarProducts;
export const selectProductDimensions = (state) =>
  state.productsInfo.productDimensions;

export default productsSlice.reducer;
